// services/historicoService.ts - Histórico via API oficial da plataforma

import { AtividadeHistorico, InputProfessor, ResponseDinamica } from '../types/index';
import api from './api';

/**
 * Normaliza data vinda da API REST: Firestore Timestamp serializa como
 * `{ seconds, nanoseconds }` sem `.toDate()` — `new Date(objeto)` vira Invalid Date.
 */
function parseActivityTimestamp(value: unknown): string {
  const fallback = (): string => new Date().toISOString();

  if (value == null) return fallback();

  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? fallback() : d.toISOString();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? fallback() : value.toISOString();
  }

  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    if (typeof o.toDate === 'function') {
      try {
        const d = (o as { toDate: () => Date }).toDate();
        return Number.isNaN(d.getTime()) ? fallback() : d.toISOString();
      } catch {
        return fallback();
      }
    }
    const sec =
      typeof o.seconds === 'number'
        ? o.seconds
        : typeof o._seconds === 'number'
          ? o._seconds
          : undefined;
    const nsec =
      typeof o.nanoseconds === 'number'
        ? o.nanoseconds
        : typeof o._nanoseconds === 'number'
          ? o._nanoseconds
          : 0;
    if (sec !== undefined) {
      const d = new Date(sec * 1000 + nsec / 1e6);
      return Number.isNaN(d.getTime()) ? fallback() : d.toISOString();
    }
  }

  const d = new Date(value as string | number);
  return Number.isNaN(d.getTime()) ? fallback() : d.toISOString();
}

/**
 * Mapeia os dados da API (activities) para o formato de UI.
 */
const mapApiToAtividade = (data: any): AtividadeHistorico => {
  const raw =
    data.createdAt ?? data.timestamp ?? data.requisicao?.timestamp ?? null;
  return {
    id: data.id,
    timestamp: parseActivityTimestamp(raw),
    input: data.config || data.input,
    resultado: data.result || data.output,
    favorito: !!data.favorito,
  };
};

/**
 * Mantido por compatibilidade de interface local do frontend.
 * O backend salva activities automaticamente na geracao.
 */
export async function salvarAtividade(
  input: InputProfessor,
  resultado: ResponseDinamica
): Promise<AtividadeHistorico | null> {
  return null;
}

/**
 * Lista atividades do professor via API oficial. A falha sobe para a tela, que diz "Não conseguimos
 * carregar as suas dinâmicas agora." (antes virava lista vazia, e parecia que as dinâmicas tinham sumido).
 */
export async function listarAtividades(): Promise<AtividadeHistorico[]> {
  const response = await api.get<{ activities: any[] }>('/api/tools/dinamica/activities');
  return (response.data.activities || []).map(mapApiToAtividade);
}

/**
 * Obtém uma atividade específica por ID
 */
export async function obterAtividade(id: string): Promise<AtividadeHistorico | null> {
  try {
    // A UI atual consulta lista em memoria e nao precisa endpoint de detalhe.
    return null; 
  } catch (error) {
    return null;
  }
}

/**
 * Deleta uma atividade por ID
 */
export async function deletarAtividade(id: string): Promise<boolean> {
  try {
    await api.delete(`/api/tools/dinamica/activities/${id}`);
    return true;
  } catch (error) {
    console.error('Erro ao deletar atividade via API:', error);
    return false;
  }
}

/**
 * Limpa todo o histórico deletando as activities do usuario
 */
export async function limparHistorico(): Promise<boolean> {
  try {
    const atividades = await listarAtividades();
    // Executa deleção em batch (cuidado com performance em centenas de items)
    const promises = atividades.map(a => deletarAtividade(a.id));
    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error('Erro ao limpar histórico da Cloud:', error);
    return false;
  }
}

/**
 * Alterna o estado de favorito de uma atividade via updateDoc
 */
export async function toggleFavorito(_id: string, _currentStatus: boolean): Promise<boolean> {
  try {
    // Favoritos ainda nao possuem endpoint oficial no backend.
    // Mantemos falso para evitar escrita direta fora do contrato.
    return false;
  } catch (error) {
    console.error('Erro ao alternar favorito:', error);
    return false;
  }
}

/**
 * Obtém estatísticas do histórico
 */
export async function obterEstatisticas(): Promise<{
  total: number;
  favoritos: number;
  tamanhoAproximado: number;
}> {
  try {
    const atividades = await listarAtividades();
    return {
      total: atividades.length,
      favoritos: atividades.filter(a => a.favorito).length,
      tamanhoAproximado: atividades.length * 15 * 1024 // Estimativa de 15kb
    };
  } catch (error) {
    return { total: 0, favoritos: 0, tamanhoAproximado: 0 };
  }
}

